import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { SectionHeader, EASE } from "@/components/Reveal";

const PLANS = [
  {
    name: "Foundation",
    price: "£495",
    period: "/month",
    tagline: "Your first AI employees.",
    features: [
      "3 AI employees of your choice",
      "1 communication channel",
      "Standard integrations",
      "Email support",
      "Monthly performance report",
    ],
    featured: false,
  },
  {
    name: "Growth",
    price: "£995",
    period: "/month",
    tagline: "A company that never sleeps.",
    features: [
      "7 AI employees",
      "Voice Calling + Receptionist AI",
      "Every channel — WhatsApp, email, social",
      "CRM and accounting integrations",
      "Priority support",
      "Weekly optimisation",
    ],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "The full workforce.",
    features: [
      "All 13 AI employees",
      "Custom integrations and APIs",
      "Dedicated success manager",
      "White-glove onboarding",
      "99.99% uptime SLA",
    ],
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative mx-auto max-w-7xl px-6 py-32 lg:px-10"
    >
      <div className="absolute left-1/2 top-1/3 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,192,138,0.06),transparent_65%)] blur-3xl" />
      <SectionHeader
        align="center"
        label="Workforce Plans"
        title={
          <>
            Hire Your Workforce.{" "}
            <span className="gold-text">Not Your Overheads.</span>
          </>
        }
        sub="Every plan runs 24 hours a day, 365 days a year. No salaries, no sick days, no ceiling."
      />

      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
        {PLANS.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
            whileHover={{ y: -8 }}
            className={`card-glow-hover relative flex flex-col rounded-2xl p-9 ${
              p.featured
                ? "glass-panel border-gold/45 shadow-[0_0_70px_rgba(216,192,138,0.18)] md:-my-4 md:py-12"
                : "glass-panel"
            }`}
            data-testid={`pricing-card-${p.name.toLowerCase()}`}
          >
            {p.featured && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gold px-4 py-1.5 font-mono text-[9px] font-medium uppercase tracking-[0.25em] text-[#050505] shadow-[0_0_25px_rgba(216,192,138,0.5)]">
                Most Popular
              </span>
            )}
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
              {p.name}
            </p>
            <div className="mt-5 flex items-baseline gap-1.5">
              <span className="font-display text-5xl font-light tracking-tight text-[#F5F5F5]">
                {p.price}
              </span>
              {p.period && (
                <span className="font-mono text-xs text-[#A3A3A3]">{p.period}</span>
              )}
            </div>
            <p className="mt-3 text-sm text-[#A3A3A3]">{p.tagline}</p>
            <div className="my-7 h-px bg-gradient-to-r from-gold/30 to-transparent" />
            <ul className="flex-1 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#C9C9C9]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              onClick={() => window.dispatchEvent(new CustomEvent("open-demo-modal"))}
              className={`group mt-9 flex items-center justify-center gap-2.5 rounded-full py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] transition-[box-shadow,background-color,transform,color] duration-300 active:scale-95 ${
                p.featured
                  ? "bg-gold text-[#050505] hover:bg-gold-bright hover:shadow-[0_0_45px_rgba(216,192,138,0.5)]"
                  : "border border-gold/40 text-gold hover:bg-gold/10 hover:shadow-[0_0_30px_rgba(216,192,138,0.2)]"
              }`}
            >
              Start Building
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        ))}
      </div>

      <p className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
        Every plan — 24 hours a day · 365 days a year · Cancel anytime
      </p>
    </section>
  );
}
